import axios from 'axios';

const BASE_URL =  'https://bloomnav.com';
//const BASE_URL =  'http://localhost:8092'
const adminApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response && (error.response.status === 401 || error.response.status === 403)) {
      try {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminProfile');
      } catch (_) {}
    }
    return Promise.reject(error);
  }
);

adminApi.fetchBillingCompanies = async () => {
  const { data } = await adminApi.get('/api/admin/billing/companies');
  return data;
};

adminApi.fetchCompanyBillingPayments = async (companyId) => {
  const { data } = await adminApi.get(`/api/admin/billing/companies/${companyId}/payments`);
  const raw = Array.isArray(data?.bills)
    ? data.bills
    : (Array.isArray(data?.payments) ? data.payments : []);
  // Accept Attendance bills and legacy PlanPayment history rows from the admin API.
  const bills = raw.filter((bill) => {
    if (!bill || typeof bill !== 'object') return false;
    if (bill.source === 'attendance_bill' || bill.source === 'plan_payment') return true;
    if (bill.kind != null || bill.periodStart != null || bill.periodEnd != null) return true;
    if (bill.totalAmount != null && bill.status != null) return true;
    return false;
  });
  return { ...data, bills };
};

adminApi.updateCompanyTrial = async (companyId, payload) => {
  const { data } = await adminApi.put(`/api/admin/billing/companies/${companyId}/trial`, payload);
  return data;
};

adminApi.updateCompanyBillingMode = async (companyId, billingMode) => {
  const { data } = await adminApi.put(`/api/admin/billing/companies/${companyId}/billing-mode`, { billingMode });
  return data;
};

adminApi.fetchAttendanceBillingDocuments = async (companyId) => {
  const { data } = await adminApi.get(`/api/admin/billing/companies/${companyId}/attendance-documents`);
  return data;
};

adminApi.ocrAttendanceBillingDocument = async (companyId, file) => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await adminApi.post(
    `/api/admin/billing/companies/${companyId}/attendance-documents/ocr`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

adminApi.createAttendanceBillingDocument = async (companyId, payload = {}) => {
  const { data } = await adminApi.post(
    `/api/admin/billing/companies/${companyId}/attendance-documents`,
    payload
  );
  return data;
};

adminApi.updateAttendanceBillingDocument = async (companyId, documentId, payload = {}) => {
  const { data } = await adminApi.put(
    `/api/admin/billing/companies/${companyId}/attendance-documents/${documentId}`,
    payload
  );
  return data;
};

adminApi.deleteAttendanceBillingDocument = async (companyId, documentId) => {
  const { data } = await adminApi.delete(
    `/api/admin/billing/companies/${companyId}/attendance-documents/${documentId}`
  );
  return data;
};

adminApi.fetchUsageAccounts = async () => {
  const { data } = await adminApi.get('/api/admin/usage/accounts');
  return data;
};

adminApi.fetchAiExpenses = async (params = {}) => {
  const { data } = await adminApi.get('/api/admin/ai-expenses', { params });
  return data;
};

adminApi.updateUsageAccount = async (companyId, payload) => {
  const { data } = await adminApi.put(`/api/admin/usage/accounts/${companyId}`, payload);
  return data;
};

adminApi.fetchPlatformConfig = async () => {
  const { data } = await adminApi.get('/api/admin/platform-config');
  return data;
};

adminApi.updatePlatformConfig = async (payload) => {
  const { data } = await adminApi.put('/api/admin/platform-config', payload);
  return data;
};

adminApi.fetchLeadsBillingAccounts = async () => {
  const { data } = await adminApi.get('/api/admin/leads-billing/accounts');
  return data;
};

adminApi.updateLeadsBillingAccount = async (companyId, payload) => {
  const { data } = await adminApi.put(`/api/admin/leads-billing/accounts/${companyId}`, payload);
  return data;
};

adminApi.bulkUpdateLeadsBillingAccounts = async (payload) => {
  const { data } = await adminApi.post('/api/admin/leads-billing/accounts/bulk', payload);
  return data;
};

adminApi.fetchLeadsBillingLedger = async (companyId, params = {}) => {
  const { data } = await adminApi.get(`/api/admin/leads-billing/accounts/${companyId}/ledger`, {
    params,
  });
  return data;
};

adminApi.fetchLeadsBillingPayments = async (params = {}) => {
  const { data } = await adminApi.get('/api/admin/leads-billing/payments', { params });
  return data;
};

adminApi.fetchLeadsBillingPayment = async (id) => {
  const { data } = await adminApi.get(`/api/admin/leads-billing/payments/${id}`);
  return data;
};

adminApi.leadsBillingInvoiceUrl = (id) =>
  `${BASE_URL}/api/admin/leads-billing/payments/${id}/invoice`;

adminApi.searchLocationUsers = async (q) => {
  const { data } = await adminApi.get('/api/admin/location/users/search', { params: { q } });
  return data;
};

adminApi.fetchLocationUserDay = async (userId, params = {}) => {
  const { data } = await adminApi.get(`/api/admin/location/users/${userId}/day`, { params });
  return data;
};

export default adminApi;

